import { ChevronDownIcon, CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverContentProps,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EntityReferenceModel } from "../clients/KompasApi";
import { Multiselect } from "./Multiselect";

interface Props<T extends EntityReferenceModel> {
  allowSearch?: boolean;
  searchPlaceholder?: string;
  items: T[];
  onChange?: (selectedItems: T[]) => void;
  onToggle?: (item: T) => void;
  popoverWidth?: PopoverContentProps["w"];
  maxH?: BoxProps["maxH"];
  maxW?: BoxProps["maxW"];
  multiple?: boolean;
  value: T[];
  showResultCount?: boolean;
  title: string;
  resetText?: string;
  valueDisplay?: React.ReactNode | ((selectedItems: T[]) => React.ReactNode);
}

export function Dropdown<T extends EntityReferenceModel>({
  allowSearch = false,
  searchPlaceholder = "Zoeken...",
  items,
  onChange,
  onToggle,
  popoverWidth,
  maxH,
  maxW,
  multiple = false,
  value,
  showResultCount = false,
  title,
  resetText = "Reset",
  valueDisplay = value.join(", "),
}: Props<T>) {
  const [searchValue, setSearchValue] = useState("");
  const { isOpen, onToggle: onPopoverToggle, onClose } = useDisclosure();

  useEffect(() => {
    if (!allowSearch) {
      setSearchValue("");
    }
  }, [allowSearch]);

  const toggleItem = useCallback(
    (item: T) => {
      if (!multiple) {
        onToggle?.(item);
        onChange?.(value.includes(item) ? [] : [item]);
        return;
      }

      const selected = new Set(value.map((i) => i.id));

      if (selected.has(item.id)) {
        selected.delete(item.id);
      } else {
        selected.add(item.id);
      }

      onToggle?.(item);
      onChange?.(items.filter((i) => selected.has(i.id)));
    },
    [value, onChange, items, onToggle, multiple]
  );

  const clear = useCallback(() => {
    if (onToggle) {
      value.forEach((i) => onToggle(i));
    }
    onChange?.([]);
  }, [value, onToggle, onChange]);

  const filteredItems = useMemo(
    () =>
      items.filter((i) =>
        i.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [items, searchValue]
  );

  const DisplayValue = useCallback(() => {
    if (typeof valueDisplay === "function") {
      return <>{valueDisplay(value)}</>;
    }
    return <>{valueDisplay}</>;
  }, [valueDisplay, value]);

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
      <PopoverAnchor>
        <Flex
          alignItems="center"
          border="1px"
          borderColor="gray.300"
          borderRadius={4}
          cursor="pointer"
          gap={2}
          justifyContent="space-between"
          maxW={maxW}
          mb={2}
          onClick={onPopoverToggle}
          p={2}
        >
          <DisplayValue />
          <PopoverTrigger>
            <ChevronDownIcon />
          </PopoverTrigger>
        </Flex>
      </PopoverAnchor>
      <PopoverContent borderRadius={8} w={popoverWidth}>
        <PopoverArrow />
        <PopoverHeader
          alignItems="baseline"
          as={Flex}
          border="none"
          gap={4}
          pt={4}
          px={4}
        >
          {title}{" "}
          <Button onClick={clear} size="xs" variant="link">
            {resetText}
          </Button>
          <PopoverCloseButton color="gray.600" right={3} top={4} />
        </PopoverHeader>
        <PopoverBody>
          {allowSearch && (
            <Flex mx={1}>
              <InputGroup>
                <InputLeftElement>
                  <SearchIcon color="gray.600" />
                </InputLeftElement>
                <Input
                  _placeholder={{
                    fontStyle: "italic",
                  }}
                  borderRadius="md"
                  mb={2}
                  name="search-customers"
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={searchPlaceholder}
                  value={searchValue}
                />
                <InputRightElement>
                  <CloseIcon
                    boxSize={2.5}
                    color="gray.600"
                    cursor="pointer"
                    onClick={() => setSearchValue("")}
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>
          )}
          {showResultCount && (
            <Text fontSize="xs" mx={1}>
              {filteredItems.length}{" "}
              {filteredItems.length === 1 ? "resultaat" : "resultaten"}
            </Text>
          )}
          <Box maxH={maxH} overflowY="auto">
            {multiple ? (
              <Multiselect
                items={filteredItems}
                onChange={onChange}
                onToggle={onToggle}
                value={value}
              />
            ) : (
              <RadioGroup>
                {filteredItems.map((item) => (
                  <Box key={item.id}>
                    <Radio
                      key={item.id}
                      isChecked={value.includes(item)}
                      onChange={() => toggleItem(item)}
                      paddingInlineStart={1}
                      width="100%"
                    >
                      {item.name}
                    </Radio>
                  </Box>
                ))}
              </RadioGroup>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
